import { Swiper, SwiperSlide } from "swiper/react"
import { Pagination } from "swiper/modules"
import { X } from "lucide-react"
import "swiper/css"
import "swiper/css/pagination"

import { Card } from "@/components/ui-kit/card"
import { MetroIcon } from "@/components/icons"
import { cn, parseJsonProperty } from "@/lib/utils"
import type { Facility } from "@/types/warehouse"
import { Button } from "@/components/ui/button"

export type FacilityCardProps = {
  facility?: Facility
  active?: boolean
  onClose?: () => void
  className?: string
}

export function FacilityCard({
  facility,
  active = true,
  onClose,
  className,
}: FacilityCardProps) {
  const subway = parseJsonProperty<{ name: string; color?: string } | null>(
    facility?.properties?.find((property) => property.code === "subway")?.value,
    null,
  )
  const displayName = facility?.properties?.find(
    (property) => property.code === "display_name",
  )?.value
  const tags = parseJsonProperty(
    facility?.properties?.find((property) => property.code === "capabilities")
      ?.value,
    [] as string[],
  )

  const images = parseJsonProperty(
    facility?.properties?.find((property) => property.code === "images")?.value,
    [] as string[],
  )

  return (
    <Card
      className={cn(
        "relative z-[1001] hidden lg:min-h-[300px] min-h-[200px] lg:h-[300px] h-[200px] p-2 shadow-md rounded-[8px] w-[calc(100%-16px)] absolute bottom-2 left-2",
        active && "block",
        className,
      )}
    >
      <Button
        variant="link"
        className="absolute right-2 top-2 z-10 size-6 p-0 border-0 cursor-pointer"
        onClick={onClose}
        aria-label="Закрыть"
      >
        <X className="size-6 text-light-muted" strokeWidth={2} />
      </Button>

      <div className="grid h-full grid-cols-[284px_1fr] gap-5">

        <div className="lg:flex hidden">
          <Swiper
            modules={[Pagination]}
            pagination={{ clickable: true }}
            className={cn(
              "w-full overflow-hidden bg-muted rounded-[6px]",
              " [&_.swiper-pagination]:mt-3.5",
              "[&_.swiper-pagination]:flex [&_.swiper-pagination]:justify-center [&_.swiper-pagination]:gap-[3.5px]",
              "[&_.swiper-pagination-bullet]:!m-0 [&_.swiper-pagination-bullet]:!h-1 [&_.swiper-pagination-bullet]:!w-9",
              "[&_.swiper-pagination-bullet]:!rounded-sm [&_.swiper-pagination-bullet]:!bg-muted",
              "[&_.swiper-pagination-bullet-active]:!bg-brand",
            )}
          >
            {images.map((src: string, index: number) => (
              <SwiperSlide key={index}>
                <img src={src} alt="" className="block min-h-[284px] w-full rounded-[6px] object-cover" />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        <div className="flex min-h-0 flex-col lg:py-6 py-2">
          <div className="relative grow">
            <h3 className="lg:mb-2 mb-1 lg:text-[32px] text-[24px] font-semibold lg:leading-[40px] leading-[32px] text-foreground">
              <span className="text-foreground">
                {displayName ?? facility?.name}
              </span>
            </h3>

            {subway && (
              <p className="lg:mb-[18px] mb-3 flex flex-wrap items-center gap-[6px] lg:text-[18px] text-[14px] lg:leading-6 leading-[22px] text-light-muted">
                <MetroIcon size={20} style={subway.color ? { color: subway.color } : undefined} />
                <span>{subway.name}</span>
              </p>
            )}

            {tags.length > 0 && (
              <ul className="flex flex-wrap gap-2">
                {tags.map((tag: string, index: number) => (
                  <li
                    key={index}
                    className="flex items-center gap-1 rounded-[2px] bg-background px-2 py-1 text-xs leading-4 text-light-muted"
                  >
                    <span>{tag}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </Card>
  )
}
